/**
 * BasketballCourtSVG — Shared, dimensionally-accurate NBA basketball court.
 *
 * Coordinate system: 1 SVG unit = 1 foot.  ViewBox = "0 0 94 50".
 * All markings follow official NBA/FIBA geometry:
 *   - Court: 94 ft × 50 ft
 *   - Paint (key): 16 ft wide × 19 ft deep
 *   - Three-point arc: 23.75 ft radius, 3 ft straight corners
 *   - Free-throw circle: 6 ft radius (solid front / dashed back)
 *   - Restricted area: 4 ft radius
 *   - Center circle: 6 ft radius
 *   - Backboard: 6 ft wide at 4 ft from baseline
 *   - Rim: 0.75 ft radius at 5.25 ft from baseline
 *
 * Usage:
 *   <BasketballCourtSVG className="w-full h-auto" />
 *   <BasketballCourtSVG className="absolute inset-0 w-full h-full" />
 */
import React from "react";

/* ------------------------------------------------------------------ */
/*  NBA court dimensions (feet)                                        */
/* ------------------------------------------------------------------ */
const CW = 94; // court length
const CH = 50; // court width
const HALF = CW / 2; // 47
const MID = CH / 2; // 25

// Basket
const BKT = 5.25; // basket center from baseline
const RIM_R = 0.75; // rim radius (18″ diameter)

// Backboard
const BKBD = 4; // backboard face from baseline
const BKBD_W = 6; // backboard width

// Paint (key / free-throw lane)
const PD = 19; // paint depth from baseline
const PW = 16; // paint width (centered)

// Free-throw circle
const FT_R = 6;

// Three-point line
const TP_R = 23.75; // arc radius from basket center
const TP_SIDE = 3; // straight section offset from sideline

// Center circle
const CC_R = 6;

// Restricted area (no-charge zone)
const RA_R = 4;

/* ------------------------------------------------------------------ */
/*  Derived geometry                                                   */
/* ------------------------------------------------------------------ */
const PY0 = (CH - PW) / 2; // 17 – top edge of paint
const PY1 = PY0 + PW; // 33 – bottom edge of paint

// Left / right basket x
const LB = BKT; // 5.25
const RB = CW - BKT; // 88.75

// Backboard x
const LBK = BKBD; // 4
const RBK = CW - BKBD; // 90

// Three-point corner intersection: where arc meets y = TP_SIDE
const TP_DY = MID - TP_SIDE; // 22
const TP_DX = Math.sqrt(TP_R * TP_R - TP_DY * TP_DY); // ≈ 8.95
const L_TC = LB + TP_DX; // ≈ 14.20
const R_TC = RB - TP_DX; // ≈ 79.80

// Lane hash marks — distance from each baseline (ft)
const HASHES = [7, 8, 11, 14];
const HASH_L = 0.6; // tick length outside paint

// Plank stripe count
const PLANK_COUNT = 12;
const PLANK_W = CW / PLANK_COUNT; // ≈ 7.83

/* ------------------------------------------------------------------ */
/*  Rounding helper                                                    */
/* ------------------------------------------------------------------ */
const r = (n: number) => Math.round(n * 100) / 100;

/* ------------------------------------------------------------------ */
/*  Pre-computed SVG paths                                             */
/* ------------------------------------------------------------------ */

// Three-point lines (straight corners + arc)
const LEFT_TP = `M 0 ${TP_SIDE} L ${r(L_TC)} ${TP_SIDE} A ${TP_R} ${TP_R} 0 0 1 ${r(L_TC)} ${CH - TP_SIDE} L 0 ${CH - TP_SIDE}`;
const RIGHT_TP = `M ${CW} ${TP_SIDE} L ${r(R_TC)} ${TP_SIDE} A ${TP_R} ${TP_R} 0 0 0 ${r(R_TC)} ${CH - TP_SIDE} L ${CW} ${CH - TP_SIDE}`;

// Free-throw circles – solid exterior half / dashed interior half
const L_FT_SOLID = `M ${PD} ${MID - FT_R} A ${FT_R} ${FT_R} 0 0 1 ${PD} ${MID + FT_R}`;
const L_FT_DASH = `M ${PD} ${MID + FT_R} A ${FT_R} ${FT_R} 0 0 1 ${PD} ${MID - FT_R}`;
const R_FT_SOLID = `M ${CW - PD} ${MID - FT_R} A ${FT_R} ${FT_R} 0 0 0 ${CW - PD} ${MID + FT_R}`;
const R_FT_DASH = `M ${CW - PD} ${MID + FT_R} A ${FT_R} ${FT_R} 0 0 0 ${CW - PD} ${MID - FT_R}`;

// Restricted area arcs (+ connecting lines to baseline)
const L_RA = `M 0 ${MID - RA_R} L ${LB} ${MID - RA_R} A ${RA_R} ${RA_R} 0 0 1 ${LB} ${MID + RA_R} L 0 ${MID + RA_R}`;
const R_RA = `M ${CW} ${MID - RA_R} L ${RB} ${MID - RA_R} A ${RA_R} ${RA_R} 0 0 0 ${RB} ${MID + RA_R} L ${CW} ${MID + RA_R}`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

interface BasketballCourtSVGProps {
  className?: string;
  /** Hardwood fill colour */
  courtColor?: string;
  /** Court-line stroke colour */
  lineColor?: string;
  /** Court-line opacity (0–1) */
  lineOpacity?: number;
  /** Court-line stroke width (SVG units) */
  lineWidth?: number;
  /** Show alternating wood-plank stripes */
  showPlanks?: boolean;
  /** Render children inside the SVG (overlays) */
  children?: React.ReactNode;
  "data-testid"?: string;
}

const BasketballCourtSVG: React.FC<BasketballCourtSVGProps> = ({
  className = "",
  courtColor = "#C17929",
  lineColor = "#ffffff",
  lineOpacity = 0.55,
  lineWidth = 0.4,
  showPlanks = true,
  children,
  "data-testid": testId,
}) => {
  const sw = lineWidth;

  return (
    <svg
      viewBox={`0 0 ${CW} ${CH}`}
      className={className}
      preserveAspectRatio="xMidYMid meet"
      data-testid={testId}
    >
      {/* ── Court surface ── */}
      <rect x={0} y={0} width={CW} height={CH} fill={courtColor} rx={0.5} />

      {/* ── Alternating plank stripes ── */}
      {showPlanks &&
        Array.from({ length: PLANK_COUNT }, (_, i) =>
          i % 2 === 0 ? (
            <rect
              key={`plank-${i}`}
              x={i * PLANK_W}
              y={0}
              width={PLANK_W}
              height={CH}
              fill="rgba(0,0,0,0.04)"
            />
          ) : null,
        )}

      {/* ── Court markings ── */}
      <g
        stroke={lineColor}
        strokeOpacity={lineOpacity}
        strokeWidth={sw}
        fill="none"
      >
        {/* Boundary */}
        <rect x={0} y={0} width={CW} height={CH} rx={0.3} />

        {/* Half-court line */}
        <line x1={HALF} y1={0} x2={HALF} y2={CH} />

        {/* Center circle */}
        <circle cx={HALF} cy={MID} r={CC_R} />

        {/* ─── Left half ─── */}

        {/* Paint (key) */}
        <rect x={0} y={PY0} width={PD} height={PW} />

        {/* Free-throw circle — solid exterior half */}
        <path d={L_FT_SOLID} />
        {/* Free-throw circle — dashed interior half */}
        <path d={L_FT_DASH} strokeDasharray="1.2 0.8" />

        {/* Three-point line (corners + arc) */}
        <path d={LEFT_TP} />

        {/* Restricted area arc */}
        <path d={L_RA} />

        {/* Backboard */}
        <line
          x1={LBK}
          y1={MID - BKBD_W / 2}
          x2={LBK}
          y2={MID + BKBD_W / 2}
          strokeWidth={sw * 1.8}
        />

        {/* Basket rim */}
        <circle cx={LB} cy={MID} r={RIM_R} />

        {/* Lane hash marks — left paint */}
        {HASHES.map((d) => (
          <React.Fragment key={`lh-${d}`}>
            <line x1={d} y1={PY0} x2={d} y2={PY0 - HASH_L} />
            <line x1={d} y1={PY1} x2={d} y2={PY1 + HASH_L} />
          </React.Fragment>
        ))}

        {/* ─── Right half (mirrored) ─── */}

        {/* Paint (key) */}
        <rect x={CW - PD} y={PY0} width={PD} height={PW} />

        {/* Free-throw circle — solid exterior half */}
        <path d={R_FT_SOLID} />
        {/* Free-throw circle — dashed interior half */}
        <path d={R_FT_DASH} strokeDasharray="1.2 0.8" />

        {/* Three-point line (corners + arc) */}
        <path d={RIGHT_TP} />

        {/* Restricted area arc */}
        <path d={R_RA} />

        {/* Backboard */}
        <line
          x1={RBK}
          y1={MID - BKBD_W / 2}
          x2={RBK}
          y2={MID + BKBD_W / 2}
          strokeWidth={sw * 1.8}
        />

        {/* Basket rim */}
        <circle cx={RB} cy={MID} r={RIM_R} />

        {/* Lane hash marks — right paint */}
        {HASHES.map((d) => (
          <React.Fragment key={`rh-${d}`}>
            <line x1={CW - d} y1={PY0} x2={CW - d} y2={PY0 - HASH_L} />
            <line x1={CW - d} y1={PY1} x2={CW - d} y2={PY1 + HASH_L} />
          </React.Fragment>
        ))}
      </g>

      {/* Center dot */}
      <circle
        cx={HALF}
        cy={MID}
        r={0.4}
        fill={lineColor}
        fillOpacity={lineOpacity}
      />

      {children}
    </svg>
  );
};

export default BasketballCourtSVG;
